<script>
/* eslint no-console: 0 */
import { useVuelidate } from '@vuelidate/core';
import { required, minLength } from '@vuelidate/validators';
import { useAlert } from 'dashboard/composables';
import WootMessageEditor from 'dashboard/components/widgets/WootWriter/Editor.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import Modal from '../../../../components/Modal.vue';
import { hasCannedContentOrFiles } from 'dashboard/helper/cannedResponseHelper';

export default {
  components: {
    NextButton,
    Modal,
    WootMessageEditor,
  },
  props: {
    id: { type: Number, default: null },
    edcontent: { type: String, default: '' },
    edshortCode: { type: String, default: '' },
    existingFiles: { type: Array, default: () => [] },
    onClose: { type: Function, default: () => {} },
  },
  setup() {
    return { v$: useVuelidate() };
  },
  data() {
    return {
      editCanned: {
        showAlert: false,
        showLoading: false,
      },
      shortCode: this.edshortCode,
      content: this.edcontent,
      files: [],
      currentFiles: [...this.existingFiles],
      removeFileIds: [],
      show: true,
    };
  },
  validations: {
    shortCode: {
      required,
      minLength: minLength(2),
    },
  },
  computed: {
    pageTitle() {
      return `${this.$t('CANNED_MGMT.EDIT.TITLE')} - ${this.edshortCode}`;
    },
    hasContentOrFiles() {
      return hasCannedContentOrFiles(
        this.content,
        this.files,
        this.currentFiles
      );
    },
  },
  methods: {
    setPageName({ name }) {
      this.v$.content.$touch();
      this.content = name;
    },
    resetForm() {
      this.shortCode = '';
      this.content = '';
      this.files = [];
      this.removeFileIds = [];
      this.v$.shortCode.$reset();
    },
    onFileChange(event) {
      const selectedFiles = Array.from(event.target.files);
      this.files = [...this.files, ...selectedFiles];
    },
    removeNewFile(index) {
      this.files.splice(index, 1);
    },
    removeExistingFile(fileId) {
      this.removeFileIds.push(fileId);
      this.currentFiles = this.currentFiles.filter(f => f.id !== fileId);
    },
    formatFileSize(bytes) {
      if (bytes < 1024) return bytes + ' B';
      if (bytes < 1048576) return (bytes / 1024).toFixed(1) + ' KB';
      return (bytes / 1048576).toFixed(1) + ' MB';
    },
    editCannedResponse() {
      // Show loading on button
      this.editCanned.showLoading = true;
      // Make API Calls
      this.$store
        .dispatch('updateCannedResponse', {
          id: this.id,
          short_code: this.shortCode,
          content: this.content,
          files: this.files,
          remove_file_ids: this.removeFileIds,
        })
        .then(() => {
          // Reset Form, Show success message
          this.editCanned.showLoading = false;
          useAlert(this.$t('CANNED_MGMT.EDIT.API.SUCCESS_MESSAGE'));
          this.resetForm();
          setTimeout(() => {
            this.onClose();
          }, 10);
        })
        .catch(error => {
          this.editCanned.showLoading = false;
          const errorMessage =
            error?.message || this.$t('CANNED_MGMT.EDIT.API.ERROR_MESSAGE');
          useAlert(errorMessage);
        });
    },
  },
};
</script>

<template>
  <Modal v-model:show="show" :on-close="onClose">
    <div class="flex flex-col h-auto overflow-auto">
      <woot-modal-header :header-title="pageTitle" />
      <form class="flex flex-col w-full" @submit.prevent="editCannedResponse()">
        <div class="w-full">
          <label :class="{ error: v$.shortCode.$error }">
            {{ $t('CANNED_MGMT.EDIT.FORM.SHORT_CODE.LABEL') }}
            <input
              v-model="shortCode"
              type="text"
              :placeholder="$t('CANNED_MGMT.EDIT.FORM.SHORT_CODE.PLACEHOLDER')"
              @input="v$.shortCode.$touch"
            />
          </label>
        </div>

        <div class="w-full">
          <label>
            {{ $t('CANNED_MGMT.EDIT.FORM.CONTENT.LABEL') }}
            <span class="text-xs text-n-slate-10">
              {{ $t('CANNED_MGMT.EDIT.FORM.CONTENT.OPTIONAL_HINT') }}
            </span>
          </label>
          <div class="editor-wrap">
            <WootMessageEditor
              v-model="content"
              class="message-editor [&>div]:px-1"
              channel-type="Context::Default"
              enable-variables
              :enable-canned-responses="false"
              :placeholder="$t('CANNED_MGMT.EDIT.FORM.CONTENT.PLACEHOLDER')"
            />
          </div>
        </div>
        <div class="w-full mt-2">
          <label>
            {{ $t('CANNED_MGMT.EDIT.FORM.FILES.LABEL') }}
          </label>
          <div class="flex items-center gap-2">
            <label
              class="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-xl cursor-pointer bg-n-alpha-2 text-n-slate-11 hover:bg-n-alpha-3 dark:hover:bg-n-solid-3 border border-n-weak"
            >
              <span class="i-ri-attachment-line size-3.5" />
              {{ $t('CANNED_MGMT.EDIT.FORM.FILES.BUTTON') }}
              <input
                type="file"
                multiple
                class="hidden"
                accept="image/*,application/pdf,.doc,.docx,.xls,.xlsx,.csv,audio/*,video/*"
                @change="onFileChange"
              />
            </label>
          </div>
          <div v-if="currentFiles.length > 0" class="mt-2 space-y-1">
            <div
              v-for="file in currentFiles"
              :key="'existing-' + file.id"
              class="flex items-center justify-between px-2 py-1 rounded bg-n-alpha-2"
            >
              <span class="text-sm truncate text-n-slate-11">
                {{ file.filename }} ({{ formatFileSize(file.byte_size) }})
              </span>
              <button
                type="button"
                class="text-n-slate-10 hover:text-red-500"
                @click="removeExistingFile(file.id)"
              >
                <span class="i-ri-close-line size-3" />
              </button>
            </div>
          </div>
          <div v-if="files.length > 0" class="mt-2 space-y-1">
            <div
              v-for="(file, index) in files"
              :key="'new-' + index"
              class="flex items-center justify-between px-2 py-1 rounded bg-blue-50 dark:bg-blue-900/20"
            >
              <span class="text-sm truncate text-n-slate-11">
                {{ file.name }} ({{ formatFileSize(file.size) }})
              </span>
              <button
                type="button"
                class="text-n-slate-10 hover:text-red-500"
                @click="removeNewFile(index)"
              >
                <span class="i-ri-close-line size-3" />
              </button>
            </div>
          </div>
        </div>

        <div class="flex flex-row justify-end w-full gap-2 px-0 py-2">
          <NextButton
            faded
            slate
            type="reset"
            :label="$t('CANNED_MGMT.EDIT.CANCEL_BUTTON_TEXT')"
            @click.prevent="onClose"
          />
          <NextButton
            type="submit"
            :label="$t('CANNED_MGMT.EDIT.FORM.SUBMIT')"
            :disabled="
              !hasContentOrFiles ||
              v$.shortCode.$invalid ||
              editCanned.showLoading
            "
            :is-loading="editCanned.showLoading"
          />
        </div>
      </form>
    </div>
  </Modal>
</template>

<style scoped lang="scss">
:deep(.ProseMirror-menubar) {
  @apply hidden;
}

:deep(.ProseMirror-woot-style) {
  @apply min-h-[12.5rem];

  p {
    @apply text-base;
  }
}
</style>
